import React, { useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { processSvgContent } from '../utils/svgUtils';
import InnerShadowFilter from './svg/InnerShadowFilter';
import OuterShadowFilter from './svg/OuterShadowFilter';
import { useSignetStudio } from '../SignetStudioContext';
import { getConfigSwitchLabel } from './ShapeAccordion';
const useStyles = makeStyles(() => ({
  svgContainer: {
    width: '100%',
    height: 'auto',
    marginTop: 8,
  },
}));

/**
 * SizePreview
 * 
 * Renders:
 *  1) A 1200x1200 SVG.
 *  2) A main path scaled & centered in the root coordinate system.
 *  3) A mask path with optional padding, also scaled & centered identically.
 *  4) An uploaded shape, also placed in the root coordinate system (no extra <g> transform),
 *     and masked so it doesn't exceed the path boundary.
 */
const SizePreview = ({ svgFile, shapeConfig, sizeConfig, shank, disabled }) => {
  const classes = useStyles();
  const { state } = useSignetStudio();
  const reversePattern = state.reversePattern;
  const configSwitchLabel = getConfigSwitchLabel(state);

  // Refs to the DOM elements
  const svgRef = useRef(null);
  const pathRef = useRef(null);         // The main displayed path
  const maskRef = useRef(null);         // The mask path that clips the shape
  const contentGroupRef = useRef(null); // A group for the user-uploaded shape
  // (or you could place the user-uploaded shape at the root <svg> level if you wish)

  const scaleRef = useRef(1); // Just to store if needed

  // Unique mask ID - include shank sku to make it unique per shank
  const maskId = React.useMemo(
    () => `shapeMask-${sizeConfig?.size_sku}-${shapeConfig?.shape_sku}-${shank?.shank_sku}`,
    [sizeConfig?.size_sku, shapeConfig?.shape_sku, shank?.shank_sku]
  );

  // Unique IDs for filters - include shank sku to make it unique per shank
  const shadowId = React.useMemo(
    () => `shadow-${sizeConfig?.size_sku}-${shapeConfig?.shape_sku}-${shank?.shank_sku}`,
    [sizeConfig?.size_sku, shapeConfig?.shape_sku, shank?.shank_sku]
  );

  useEffect(() => {
    if (!svgFile || !sizeConfig || !shapeConfig || !shank) return;

    const loadSvg = async () => {
      try {
        if (!shank) return;

        //
        // 1) Basic SVG setup
        //
        const mainSvg = svgRef.current;
        mainSvg.setAttribute('viewBox', '0 0 1200 1200');
        mainSvg.setAttribute('width', '100%');
        mainSvg.setAttribute('height', '100%');

        //
        // 2) Place & scale the main path
        //
        const mainPath = pathRef.current;
        mainPath.setAttribute('d', shank?.path);
        mainPath.setAttribute('fill', reversePattern ? (disabled ? '#666' : '#e0e0e0') : (disabled ? '#f5f5f5' : '#e0e0e0'));
        mainPath.setAttribute('stroke', disabled ? '#eee' : '#ccc');
        mainPath.setAttribute('stroke-width', '1');

        // Measure raw bounding box
        const pathBBox = mainPath.getBBox();
        if (!pathBBox || !isFinite(pathBBox.width) || !isFinite(pathBBox.height)) return;

        // Compute how to scale to match the "real" dimension:
        const maxShapeDimension = Math.max(shank?.width, shank?.height);
        const pathMaxDimension = Math.max(pathBBox.width, pathBBox.height);
        const pathScale = maxShapeDimension / pathMaxDimension;
        scaleRef.current = pathScale;

        // Center the scaled path in 1200x1200
        const scaledW = pathBBox.width * pathScale;
        const scaledH = pathBBox.height * pathScale;
        const pathTranslateX = (1200 - scaledW) / 2 - pathBBox.x * pathScale;
        const pathTranslateY = (1200 - scaledH) / 2 - pathBBox.y * pathScale;

        // Set final transform on the main path
        mainPath.setAttribute(
          'transform',
          `translate(${pathTranslateX}, ${pathTranslateY}) scale(${pathScale})`
        );

        //
        // 3) Build the mask path transform
        //
        if (maskRef.current) {
          maskRef.current.setAttribute('d', shank?.path);

          // By default, same transform as main path
          let maskTransform = `translate(${pathTranslateX}, ${pathTranslateY}) scale(${pathScale})`;

          // If there's padding, we shrink the mask around the path's center
          if (shank?.config[configSwitchLabel].padding > 0) {
            const centerX = pathBBox.x + pathBBox.width / 2;
            const centerY = pathBBox.y + pathBBox.height / 2;
            const paddingScale = 1 - shank?.config[configSwitchLabel].padding / 100;

            maskTransform = [
              `translate(${pathTranslateX}, ${pathTranslateY})`,
              `scale(${pathScale})`,
              `translate(${centerX}, ${centerY})`,
              `scale(${paddingScale})`,
              `translate(${-centerX}, ${-centerY})`,
            ].join(' ');
          }

          maskRef.current.setAttribute('transform', maskTransform);
        }

        //
        // 4) Parse and place the user-uploaded SVG
        //
        const reader = new FileReader();
        reader.onload = () => {
          const contentString = processSvgContent(reader.result, disabled ? '#eee' : reversePattern ? 'white' : '#666', true);
          const parser = new DOMParser();
          const doc = parser.parseFromString(contentString, 'image/svg+xml');
          const uploadedSvg = doc.querySelector('svg');
          if (!uploadedSvg) return;

          // Clear any old shape
          const contentGroup = contentGroupRef.current;
          contentGroup.innerHTML = '';

          // Copy child nodes from the uploaded SVG into our <g>
          Array.from(uploadedSvg.childNodes).forEach(node => {
            if (node.nodeType === 1) {
              contentGroup.appendChild(node.cloneNode(true));
            }
          });

          // Now measure the bounding box of the user shape
          const shapeBBox = contentGroup.getBBox();
          if (!shapeBBox || !isFinite(shapeBBox.width) || !isFinite(shapeBBox.height)) return;

          // Additional user scale factor (like if shank?.config[configSwitchLabel].scale = 20 => +20%)
          const userScaleFactor = 1 + (shank?.config[configSwitchLabel].scale || 0) / 100;

          // The real "available" size is basically the shank's width/height times userScaleFactor
          const availableW = shank?.width * userScaleFactor;
          const availableH = shank?.height * userScaleFactor;

          // Compute how much to scale the shape to fit in that area
          const shapeFitScale = Math.min(
            availableW / shapeBBox.width,
            availableH / shapeBBox.height
          );

          // Combine with pathScale to align with the main path in the root coordinate system
          const finalShapeScale = shapeFitScale * pathScale;

          // Center the shape in 1200×1200
          const finalW = shapeBBox.width * finalShapeScale;
          const finalH = shapeBBox.height * finalShapeScale;

          const shapeTranslateX = (1200 - finalW) / 2 - shapeBBox.x * finalShapeScale;
          const shapeTranslateY = (1200 - finalH) / 2 - shapeBBox.y * finalShapeScale;

          // Calculate shape's center point for rotation
          const shapeCenterX = shapeBBox.x + shapeBBox.width / 2;
          const shapeCenterY = shapeBBox.y + shapeBBox.height / 2;
          
          // Get rotation angle from config
          const rotation = shank?.config[configSwitchLabel].shape_rotation || 0;

          // Apply transforms in this order: translate to position, move to shape center, rotate, move back, scale
          contentGroup.setAttribute(
            'transform',
            [
              `translate(${shapeTranslateX}, ${shapeTranslateY})`,
              `translate(${shapeCenterX * finalShapeScale}, ${shapeCenterY * finalShapeScale})`,
              `rotate(${rotation})`,
              `translate(${-shapeCenterX * finalShapeScale}, ${-shapeCenterY * finalShapeScale})`,
              `scale(${finalShapeScale})`
            ].join(' ')
          );
        };
        reader.readAsText(svgFile);

      } catch (error) {
        console.error('Error rendering SVG:', error);
      }
    };

    loadSvg();
  }, [svgFile, sizeConfig, shapeConfig, shank, disabled, configSwitchLabel, reversePattern]);

  return (
    <div className={classes.svgContainer}>
      <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask
            id={maskId}
            maskUnits="userSpaceOnUse"
            maskContentUnits="userSpaceOnUse"
            x="0" y="0" width="1200" height="1200"
          >
            <rect x="0" y="0" width="1200" height="1200" fill="black" />
            <path 
              ref={maskRef} 
              fill="white"
            />
          </mask>
          <OuterShadowFilter 
            id={`${shadowId}-outer`}
            shadowColor="#000000"
            strength={shank?.config[configSwitchLabel].depth || 'standard'}
          />
          <InnerShadowFilter 
            id={`${shadowId}-inner`}
            shadowColor="#000000"
            strength={shank?.config[configSwitchLabel].depth || 'standard'}
          />
        </defs>

        {/* Main visible path (the "shank") */}
        <path ref={pathRef} />
        
        {reversePattern && (
          <path 
            d={maskRef?.current?.getAttribute('d')}
            fill="#666"
            stroke="transparent"
            transform={maskRef?.current?.getAttribute('transform')}
            filter={`url(#${shadowId}-inner)`}
          />
        )}
        
        {/* Group of the user-uploaded shape, masked so it cannot exceed the path boundary */}
        <g
          mask={shank?.config[configSwitchLabel].padding > 0 ? `url(#${maskId})` : null}
        >  
          <g 
            ref={contentGroupRef}
            filter={reversePattern ? `url(#${shadowId}-outer)` : `url(#${shadowId}-inner)`}
          /> 
        </g>
      </svg>
    </div>
  );
};

export default SizePreview;
